/**
 * FreedrIQChallengeAdmin — /admin/freedriq-challenge
 *
 * Simple admin panel to open a new Community Challenge, close the currently
 * open one, and see participant counts on past challenges.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import ToolSelector from '../../components/ToolSelector';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Trophy, Users, Loader2, Plus, X, ArrowLeft, CheckCircle2, Calendar,
} from 'lucide-react';

const FreedrIQChallengeAdmin = () => {
  const { API } = useContext(AuthContext);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [closesAt, setClosesAt] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/freedriq/challenge/admin/list`);
      setChallenges(data.challenges || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load challenges');
    } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/freedriq/challenge/admin`, {
        name:        name.trim(),
        description: description.trim() || null,
        closes_at:   closesAt || null,
      });
      toast.success('Challenge opened');
      setName(''); setDescription(''); setClosesAt('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create challenge');
    } finally { setCreating(false); }
  };

  const close = async (id) => {
    if (!window.confirm('Close this challenge? Learners will no longer be able to submit.')) return;
    try {
      await axios.put(`${API}/freedriq/challenge/admin/${id}`, { is_open: false });
      toast.success('Challenge closed');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not close');
    }
  };

  const reopen = async (id) => {
    try {
      await axios.put(`${API}/freedriq/challenge/admin/${id}`, { is_open: true });
      toast.success('Challenge reopened');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reopen');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="challenge-admin-page">
      <ToolSelector />
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <Link to="/admin/users" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Admin
          </Link>
          <div className="eyebrow text-primary inline-flex items-center gap-1.5 mt-1">
            <Trophy className="w-3 h-3" /> Community Challenge · Admin
          </div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">FreedrIQ Community Challenge</h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-xl">
            Open a challenge to give every operator on the platform a shared 125-question
            test (all tiers combined) and a live global leaderboard. Winners get a
            shareable social card.
          </p>
        </div>

        {/* Create form */}
        <div className="pro-card p-6" data-testid="challenge-admin-create">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5">
            <Plus className="w-3 h-3" /> Open a new challenge
          </div>
          <div className="grid gap-3 mt-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Feb 2026 FreezeDry Cup" data-testid="challenge-admin-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Description</label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Optional tagline shown on the splash." data-testid="challenge-admin-description" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Auto-close on (optional ISO date)</label>
              <Input value={closesAt} onChange={(e) => setClosesAt(e.target.value)} placeholder="2026-02-28T23:59:00Z" data-testid="challenge-admin-closes-at" />
            </div>
            <div>
              <Button onClick={create} disabled={creating || !name.trim()} className="rounded-full font-bold" data-testid="challenge-admin-create-btn">
                {creating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Opening…</> : <>Open challenge <Trophy className="ml-1.5 w-3.5 h-3.5" /></>}
              </Button>
            </div>
          </div>
        </div>

        {/* Existing challenges */}
        <div className="mt-6">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5 mb-3">
            <Calendar className="w-3 h-3" /> All challenges
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          ) : challenges.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No challenges yet — open your first one above.</div>
          ) : (
            <div className="space-y-2">
              {challenges.map((c) => (
                <div key={c.id} className="pro-card p-4 flex items-center gap-3" data-testid={`challenge-admin-row-${c.id}`}>
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${
                    c.is_open ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Trophy className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-extrabold text-sm truncate">{c.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {c.description || '—'}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mt-1 flex flex-wrap gap-2">
                      <span>opened {c.opened_at?.slice(0, 10)}</span>
                      {c.closed_at && <span>· closed {c.closed_at.slice(0, 10)}</span>}
                      {c.closes_at && !c.closed_at && <span>· auto-closes {c.closes_at.slice(0, 10)}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground inline-flex items-center gap-1">
                      <Users className="w-3 h-3" /> {c.participants}
                    </div>
                    {c.is_open ? (
                      <button
                        onClick={() => close(c.id)}
                        className="text-[11px] font-bold text-red-600 hover:underline mt-1 inline-flex items-center gap-1"
                        data-testid={`challenge-admin-close-${c.id}`}
                      >
                        <X className="w-3 h-3" /> Close
                      </button>
                    ) : (
                      <button
                        onClick={() => reopen(c.id)}
                        className="text-[11px] font-bold text-primary hover:underline mt-1 inline-flex items-center gap-1"
                        data-testid={`challenge-admin-reopen-${c.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3" /> Reopen
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FreedrIQChallengeAdmin;
